import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { message, Button } from 'antd';
import SelectDropdown from 'react-select';
import { GET_MANAGER_WITH_FULLNAME_EMPID_LOCATION, GET_SUPER_MANAGERS_WITH_EMPID_LOCATION_FULLNAME, GET_User_Details_User_Management_Role_Management_View, Get_User_Profile } from '../../services/userAPI';
import { AgGridReact } from 'ag-grid-react';
import 'antd/dist/reset.css';
import { gql } from '@apollo/client';
import PageBreadcrumb from '../../reusableField/PageBreadcrumb';
import CustomNoRowsOverlay from '../CustomNoRowsOverlay';

// Define the ASSIGN_USER mutation
export const ASSIGN_USER = gql`
  mutation AssignUser($userAssignInfo: [AssignUnassignUser!]!) {
    assignUser(userAssignInfo: $userAssignInfo) {
      success
      message
      data
    }
  }
`;

const RoleManagementView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userId = location.state?.userId;
    const roleId = location.state?.roleId;

    const [cursor, setCursor] = useState(null);
    const [pageSize, setPageSize] = useState(1000);
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [gridReady, setGridReady] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialAssignedUsers, setInitialAssignedUsers] = useState([]);
    const [managerFilter, setManagerFilter] = useState("Select Manager");
    const [superManagerFilter, setSuperManagerFilter] = useState("Select Super Manager");
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [managerSearchTerm, setManagerSearchTerm] = useState("");
    const [superManagerSearchTerm, setSuperManagerSearchTerm] = useState("");
    const [allManagers, setAllManagers] = useState([]);
    const [managerCursor, setManagerCursor] = useState(null);
    const [hasNextManagerPage, setHasNextManagerPage] = useState(false);
    const [allSuperManagers, setAllSuperManagers] = useState([]);
    const [superManagerCursor, setSuperManagerCursor] = useState(null);
    const [hasNextSuperManagerPage, setHasNextSuperManagerPage] = useState(false);

    // Fetch user profile data
    const { data: profileData, loading: profileLoading, error: profileError } = useQuery(Get_User_Profile, {
        variables: { userId: parseInt(userId) },
        skip: !userId,
        fetchPolicy: 'cache-and-network',
    });

    // Fetch user details for table
    const { loading: tableLoading, error: tableError, data: tableDataResponse, refetch: getRefetch } = useQuery(
        GET_User_Details_User_Management_Role_Management_View,
        {
            variables: {
                roleId: roleId || '',
                pagingCriteria: {
                    cursor: cursor,
                    limit: pageSize,
                },
                reportingIds: [], // Initially empty, updated on apply
            },
            skip: !roleId,
            fetchPolicy: 'network-only',
        }
    );

    const [fetchManagers, { data: managersData, loading: managersLoading, error: managersError }] = useLazyQuery(GET_MANAGER_WITH_FULLNAME_EMPID_LOCATION, {
        fetchPolicy: 'network-only',
    });

    const [fetchSuperManagers, { data: superManagersData, loading: superManagersLoading, error: superManagersError }] = useLazyQuery(GET_SUPER_MANAGERS_WITH_EMPID_LOCATION_FULLNAME, {
        fetchPolicy: 'network-only',
    });

    // Initial fetch for managers and super managers
    useEffect(() => {
        if (roleId) {
            fetchManagers({
                variables: {
                    roleId: parseInt(roleId) || 0,
                    searchTerm: "",
                    pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" },
                },
            });
            fetchSuperManagers({
                variables: {
                    roleId: parseInt(roleId) || 0,
                    searchTerm: "",
                    pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" },
                },
            });
        }
    }, [roleId]);

    // Fetch managers when search term changes (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (roleId) {
                fetchManagers({
                    variables: {
                        roleId: parseInt(roleId) || 0,
                        searchTerm: managerSearchTerm,
                        pagingCriteria: { limit: 50, cursor: managerCursor, sortByField: "created_at", sortOrder: "desc" },
                    },
                });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [managerSearchTerm]);

    // Fetch super managers when search term changes (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (roleId) {
                fetchSuperManagers({
                    variables: {
                        roleId: parseInt(roleId) || 0,
                        searchTerm: superManagerSearchTerm,
                        pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" },
                    },
                });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [superManagerSearchTerm]);

    // Assign User mutation
    const [assignUser] = useMutation(ASSIGN_USER, {
        onCompleted: (data) => {
            if (data.assignUser.success) {
                message.success(data.assignUser.message || 'User(s) assigned successfully');
                getRefetch();
                setInitialAssignedUsers(selectedUsers); // Update initial assignments
            } else {
                message.error(data.assignUser.message || 'Failed to assign user(s)');
            }
        },
        onError: (error) => {
            console.error('Mutation error:', error);
            message.error('Error assigning user(s): ' + error.message);
        },
    });

    // Process table data
    const tableData = useMemo(
        () =>
            tableDataResponse?.getUsersForAccount?.data?.map((user) => ({
                key: user.id,
                UserId: user.id || null,
                id: user.employeeId || '',
                // name: `${user.surname} ${user.name}`.trim(),
                name: `${user.fullname}`,
                employeeMailId: user.email || '',
                gender: user.gender || '',
                genderId: user.genderId || null,
                joiningDate: user.joiningDate || '',
                designation: user.designation || '',
                designationId: user.designationId || null,
                surname: user.surname || '',
                dateOfBirth: user.dateOfBirth || '',
                city: user.city || '',
                branch: user.branch || '',
                role: user.role || '',
                phoneNumber: user.phoneNumber || '',
                address: user.address || '',
                state: user.state || '',
                zone: user.zone || '',
                highestEducationQualification: user.highestEducationQualification || '',
                previousEmployer: user.previousEmployer || '',
                previousExperience: user.previousExperience ? Number(user.previousExperience) : 0,
                profileActivated: user.profileActivated || false,
                roleId: user.roleId || null,
                roleStartDate: user.roleStartDate || '',
                accountId: user.accountId || null,
                accountName: user.accountName || '',
                reportingDate: user.reportingDate || '',
                employeeId: user.employeeId || '',
                employeeName: user.reporting?.name || '',
                stateName: user.stateName || '',
                cityName: user.cityName || '',
                employeeType: user.employeeType || '',
                employeeTypeId: user.employeeTypeId || null,
                numberOfAssociates: user.numberOfAssociates || 0,
                numberOfManagers: user.numberOfManagers || 0,
                status: user.status || 0,
                userType: user.userType?.userType || '',
                assignedToUserId: user.assignedToUser?.id || null,
                assignedToUserName: user.assignedToUser?.name || 'Unassigned',
            })) || [],
        [tableDataResponse]
    );

    // Update managers data when fetched
    useEffect(() => {
        if (managersData?.getManagerWithFullnameEmpidLocation) {
            const newManagers = managersData.getManagerWithFullnameEmpidLocation.data || [];
            const pageMeta = managersData.getManagerWithFullnameEmpidLocation.pageMeta;
            
            // Only replace if it's a new search (cursor is null), otherwise append
            if (!managerCursor) {
                setAllManagers(newManagers);
            } else {
                setAllManagers(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const filtered = newManagers.filter(m => !existingIds.has(m.id));
                    return [...prev, ...filtered];
                });
            }
            setManagerCursor(pageMeta?.nextPage || null);
            setHasNextManagerPage(pageMeta?.hasNextPage || false);
        }
    }, [managersData, managerCursor]);

    // Update super managers data when fetched
    useEffect(() => {
        if (superManagersData?.getSuperManagersWithEmpidLocationFullname) {
            const newSuperManagers = superManagersData.getSuperManagersWithEmpidLocationFullname.data || [];
            const pageMeta = superManagersData.getSuperManagersWithEmpidLocationFullname.pageMeta;
            
            // Only replace if it's a new search (cursor is null), otherwise append
            if (!superManagerCursor) {
                setAllSuperManagers(newSuperManagers);
            } else {
                setAllSuperManagers(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const filtered = newSuperManagers.filter(m => !existingIds.has(m.id));
                    return [...prev, ...filtered];
                });
            }
            setSuperManagerCursor(pageMeta?.nextPage || null);
            setHasNextSuperManagerPage(pageMeta?.hasNextPage || false);
        }
    }, [superManagersData, superManagerCursor]);

    // Define dropdown options with IDs - show "Full Name | Employee ID | Location"
    const managerOptions = useMemo(() => {
        return allManagers.map(manager => {
            const fullName = [manager.name, manager.surname].filter(Boolean).join(' ');
            const location = manager.location || manager.cityName || '';
            const labelParts = [fullName, manager.employeeId, location].filter(Boolean);
            return { value: manager.id, label: labelParts.join(' | ') };
        });
    }, [allManagers]);

    const superManagerOptions = useMemo(() => {
        return allSuperManagers.map(superManager => {
            const fullName = [superManager.name, superManager.surname].filter(Boolean).join(' ');
            const location = superManager.location || superManager.cityName || '';
            const labelParts = [fullName, superManager.employeeId, location].filter(Boolean);
            return { value: superManager.id, label: labelParts.join(' | ') };
        });
    }, [allSuperManagers]);

    // Load more managers when scrolling to bottom of dropdown
    const handleManagerScrollToBottom = () => {
        if (hasNextManagerPage && !managersLoading && managerCursor) {
            fetchManagers({
                variables: {
                    roleId: parseInt(roleId) || 0,
                    searchTerm: managerSearchTerm,
                    pagingCriteria: { limit: 50, cursor: managerCursor, sortByField: "created_at", sortOrder: "desc" },
                },
            });
        }
    };

    // Load more super managers when scrolling to bottom of dropdown
    const handleSuperManagerScrollToBottom = () => {
        if (hasNextSuperManagerPage && !superManagersLoading && superManagerCursor) {
            fetchSuperManagers({
                variables: {
                    roleId: parseInt(roleId) || 0,
                    searchTerm: superManagerSearchTerm,
                    pagingCriteria: { limit: 50, cursor: superManagerCursor, sortByField: "created_at", sortOrder: "desc" },
                },
            });
        }
    };

    // Update filteredTableData and selected users
    useEffect(() => {
        setFilteredTableData(tableData);
        const profileUserId = parseInt(profileData?.userProfile?.id);
        // Set initial assigned users based on assignedToUserId matching profileUserId
        const newInitialAssignedUsers = tableData
            .filter((row) => row.assignedToUserId === profileUserId)
            .map((row) => row.UserId);
        setInitialAssignedUsers(newInitialAssignedUsers);
        // Set selectedUsers to match initial assignments
        setSelectedUsers(newInitialAssignedUsers);
    }, [tableData, profileData]);

    // Grid configurations
    const defaultColDef = useMemo(
        () => ({
            flex: 1,
            minWidth: 100,
            resizable: true,
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            cellStyle: { padding: '10px' },
            tooltipValueGetter: (params) => params.value,
        }),
        []
    );

    const rowClassRules = useMemo(() => ({
        'row-disabled': (params) => {
            const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
            return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null;
        },
    }), [profileData]);

    const getRowStyle = useMemo(() => (params) => {
        const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
        if (params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null) {
            return { backgroundColor: '#d3d3d3', pointerEvents: 'none', opacity: 0.5 };
        }
        return {};
    }, [profileData]);

    const columnDefs = useMemo(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                filter: false,
                sortable: false,
                minWidth: 50,
                maxWidth: 50,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
                cellRenderer: (params) => {
                    const record = params.data;
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    const isChecked = selectedUsers.includes(record.UserId);
                    const isDisabled = record.assignedToUserId !== profileUserId && record.assignedToUserId !== null;

                    return (
                        <div className="custom-checkbox">
                            <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={() => {
                                    if (!isDisabled) {
                                        const newSelectedUsers = isChecked
                                            ? selectedUsers.filter((key) => key !== record.UserId)
                                            : [...selectedUsers, record.UserId];
                                        setSelectedUsers(newSelectedUsers);
                                    }
                                }}
                                className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                                style={{ accentColor: '#AD0101' }}
                            />
                        </div>
                    );
                },
            },
            {
                headerName: 'Name',
                headerTooltip: 'Name',
                field: 'name',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Employee ID',
                headerTooltip: 'Employee ID',
                field: 'id',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Email',
                headerTooltip: 'Email',
                field: 'employeeMailId',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                valueGetter: (params) => params.data?.employeeMailId || '',
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Phone',
                headerTooltip: 'Phone',
                field: 'phoneNumber',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Designation',
                headerTooltip: 'Designation',
                field: 'designation',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Date of Joining',
                headerTooltip: 'Date of Joining',
                field: 'joiningDate',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                valueFormatter: (params) => {
                    if (!params.value) return '';
                    const date = new Date(params.value);
                    if (isNaN(date)) return params.value;
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                },
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'City',
                headerTooltip: 'City',
                field: 'cityName',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Branch',
                headerTooltip: 'Branch',
                field: 'branch',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'User Type',
                headerTooltip: 'User Type',
                field: 'userType',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                valueGetter: (params) => params.data?.userType || '',
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'App Active/Inactive',
                headerTooltip: 'App Active/Inactive',
                field: 'status',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                valueGetter: (params) => {
                    const status = params.data?.status;
                    return status === 1 ? 'Active' : 'Inactive';
                },
                cellClass: (params) => {
                    const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
                    return params.data.assignedToUserId !== profileUserId && params.data.assignedToUserId !== null ? 'ag-cell-disabled' : '';
                },
            },
            {
                headerName: 'Assign To',
                headerTooltip: 'Assign To',
                field: 'assignedToUserName',
                sortable: true,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                cellClass: 'ag-cell-assign-to',
                cellRenderer: (params) => {
                    const record = params.data;
                    return (
                        <span
                            className={record.assignedToUserId ? "cursor-pointer text-black-900" : "text-black-900"}
                            onClick={() => {
                                if (record.assignedToUserId) {
                                    navigate('/role-management-view', {
                                        state: {
                                            userId: record.assignedToUserId,
                                            roleId: location.state?.roleId,
                                            accountId: location.state?.accountId,
                                            accountName: location.state?.accountName,
                                            name: location.state?.name,
                                            dartsNeeded: location.state?.dartsNeeded,
                                            clanNeeded: location.state?.clanNeeded,
                                            syncNeeded: location.state?.syncNeeded,
                                            dartsFormNeeded: location.state?.dartsFormNeeded,
                                            pocName: location.state?.pocName,
                                            pocEmail: location.state?.pocEmail,
                                            pocNumber: location.state?.pocNumber,
                                            numberLogin: location.state?.numberLogin,
                                            emailLogin: location.state?.emailLogin,
                                            mandatoryTimeDistribution: location.state?.mandatoryTimeDistribution,
                                            targetEditDays: location.state?.targetEditDays,
                                            inputEditDays: location.state?.inputEditDays,
                                        },
                                    });
                                }
                            }}
                        >
                            {record.assignedToUserName}
                        </span>
                    );
                },
            },
        ],
        [selectedUsers, filteredTableData, profileData, navigate, location.state]
    );

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        setGridReady(true);
    };

    const onSelectionChanged = () => {
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows();
            setSelectedUsers(selectedRows.map((row) => row.UserId));
        }
    };

    const noRowsOverlayComponent = () => {
        return <CustomNoRowsOverlay message="No users found" />;
    };

    const loadingOverlayComponent = () => {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#AD0101]"></div>
                {/* <p className="mt-2 text-sm text-gray-600">Loading...</p> */}
            </div>
        );
    };

    const onFilterChanged = (params) => {
        if (params?.api) {
            const isFilterActive = params.api.isAnyFilterPresent();
            if (isFilterActive) {
                const filterModel = params.api.getFilterModel();
                let filteredData = [...filteredTableData];

                const getDisplayVal = (row, field) => {
                    if (field === "joiningDate") {
                        if (!row.joiningDate) return "";
                        const date = new Date(row.joiningDate);
                        if (isNaN(date)) return row.joiningDate;
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                    }
                    const val = row[field];
                    return val === null || val === undefined ? "" : String(val);
                };

                Object.keys(filterModel).forEach(field => {
                    const filter = filterModel[field];
                    if (filter && filter.filter) {
                        const filterValue = filter.filter.toLowerCase();
                        filteredData = filteredData.filter(row => {
                            const cellValue = getDisplayVal(row, field);
                            return cellValue.toLowerCase().includes(filterValue);
                        });
                    }
                });

                if (filteredData.length === 0) {
                    params.api.showNoRowsOverlay();
                } else {
                    params.api.hideOverlay();
                }
            } else {
                params.api.hideOverlay();
            }
        }
    };

    const handleSaveClick = async () => {
        const profileUserId = parseInt(profileData?.userProfile?.id) || 0;
        // Filter for non-disabled rows (can be checked/unchecked)
        const eligibleUsers = filteredTableData.filter(
            (row) => row.assignedToUserId === profileUserId || row.assignedToUserId === null
        );
        // Compute changes based on eligible users
        const newlyChecked = selectedUsers.filter(
            (userId) => !initialAssignedUsers.includes(userId) && eligibleUsers.some((row) => row.UserId === userId)
        );
        const newlyUnchecked = initialAssignedUsers.filter(
            (userId) => !selectedUsers.includes(userId) && eligibleUsers.some((row) => row.UserId === userId)
        );

        if (newlyChecked.length === 0 && newlyUnchecked.length === 0) {
            message.warning('No changes to save');
            return;
        }

        try {
            const userIdToAssign = parseInt(userId);
            const userAssignInfo = [
                ...newlyChecked.map((userId) => ({
                    userId: parseInt(userId),
                    assignedToId: userIdToAssign,
                    assign: true,
                })),
                ...newlyUnchecked.map((userId) => ({
                    userId: parseInt(userId),
                    assignedToId: userIdToAssign,
                    assign: false,
                })),
            ];

            await assignUser({
                variables: {
                    userAssignInfo,
                },
            });
        } catch (error) {
            console.error('Mutation error:', error);
            message.error('Error assigning user(s): ' + error.message);
        }
    };

    const handleApplyClick = async () => {
        // Find the selected manager and super manager IDs
        const selectedManager = managerOptions.find((option) => option.label === managerFilter);
        const selectedSuperManager = superManagerOptions.find((option) => option.label === superManagerFilter);
        const reportingIds = [];

        if (selectedManager && selectedManager.value) {
            reportingIds.push(parseInt(selectedManager.value));
        }
        if (selectedSuperManager && selectedSuperManager.value) {
            reportingIds.push(parseInt(selectedSuperManager.value));
        }

        try {
            await getRefetch({
                roleId: roleId || '',
                pagingCriteria: {
                    cursor: cursor,
                    limit: pageSize,
                },
                reportingIds: reportingIds.length > 0 ? reportingIds : null,
            });
            message.success('Filters applied successfully');
        } catch (error) {
            console.error('Refetch error:', error);
            message.error('Error applying filters: ' + error.message);
        }
    };

    useEffect(() => {
        if (profileError) {
            message.error('Failed to fetch user profile: ' + profileError.message);
        }
        if (tableError) {
            message.error('Failed to fetch user details: ' + tableError.message);
        }
        if (managersError) {
            message.error('Failed to fetch managers: ' + managersError.message);
        }
        if (superManagersError) {
            message.error('Failed to fetch super managers: ' + superManagersError.message);
        }
    }, [profileError, tableError, managersError, superManagersError]);

    const handleBack = () => {
        navigate('/role-management', {
            state: {
                roleId: location.state?.roleId,
                accountId: location.state?.accountId,
                accountName: location.state?.accountName,
                name: location.state?.name,
                dartsNeeded: location.state?.dartsNeeded,
                clanNeeded: location.state?.clanNeeded,
                syncNeeded: location.state?.syncNeeded,
                dartsFormNeeded: location.state?.dartsFormNeeded,
                pocName: location.state?.pocName,
                pocEmail: location.state?.pocEmail,
                pocNumber: location.state?.pocNumber,
                numberLogin: location.state?.numberLogin,
                emailLogin: location.state?.emailLogin,
                mandatoryTimeDistribution: location.state?.mandatoryTimeDistribution,
                targetEditDays: location.state?.targetEditDays,
                inputEditDays: location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate('/admin');
    };

    if (profileLoading || tableLoading) return (
        <div className="flex justify-center items-center h-screen">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
        </div>
    );

    if (!userId) return <div className="text-center text-red-600 font-inter">No user ID provided</div>;
    if (profileError) return <div className="text-center text-red-600 font-inter">Error: {profileError.message}</div>;

    const user = profileData?.userProfile;

    if (!user) return <div className="text-center text-gray-600 font-inter">No user data found</div>;

    return (
        <div className="p-4 sm:p-6 mx-auto min-h-screen">
            <style>
                {`
          .ag-cell-disabled {
            pointer-events: none !important;
            opacity: 0.5 !important;
            background-color: #d3d3d3 !important;
          }
          .ag-cell-assign-to {
            pointer-events: auto !important;
            opacity: 1 !important;
            background-color: transparent !important;
          }
          .ag-cell[col-id="assignedToUserName"] {
            pointer-events: auto !important;
            opacity: 1 !important;
            background-color: transparent !important;
          }
        `}
            </style>
            <div className="-mt-14 mb-1">
                <PageBreadcrumb homePath="/admin" onBack={handleBack}>
                    <span onClick={() => navigate('/admin')} style={{ cursor: 'pointer' }}>{location.state?.accountName}</span>
                    <span>&gt;</span>
                    <span onClick={() => navigate('/view-role', { state: location.state })} style={{ cursor: 'pointer' }}>{location.state?.name}</span>
                    <span>&gt;</span>
                    <span onClick={handleBack} style={{ cursor: 'pointer' }}>Darts</span>
                    <span>&gt;</span>
                    <span style={{ fontWeight: 'bold' }}>Clan Crew Profile</span>
                </PageBreadcrumb>
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-4 mb-1">
                <div className="flex items-center h-20 bg-white p-4 rounded-lg shadow-sm border border-gray-200 w-full">
                    <img
                        src={user.profilePicture}
                        alt={user.name}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover mr-4 sm:mr-6"
                        onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/96';
                        }}
                    />
                    <div className="flex-1">
                        <h3 className="font-inter text-base sm:text-lg font-medium text-gray-800 mb-0">{user.surname}{user.name}</h3>
                        <p className="font-inter text-sm text-gray-600">{user.email}</p>
                    </div>
                </div>
                <div className="w-full">
                    <div className="h-20 bg-white p-1 sm:p-2 rounded-lg shadow-sm border border-gray-200 flex flex-col">
                        <h2 className="font-inter text-xs sm:text-sm font-semibold text-gray-800 mb-1">Filters</h2>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                            <SelectDropdown
                                options={managerOptions}
                                value={managerOptions.find(opt => opt.label === managerFilter) || null}
                                onChange={(selected) => {
                                    const newManagerFilter = selected ? selected.label : "Select Manager";
                                    setManagerFilter(newManagerFilter);
                                    if (!selected) {
                                        if (gridApi) gridApi.showLoadingOverlay();
                                        const superManagerSelected = superManagerOptions.find(opt => opt.label === superManagerFilter);
                                        const reportingIds = superManagerSelected ? [parseInt(superManagerSelected.value)] : [];
                                        getRefetch({
                                            roleId: roleId || '',
                                            pagingCriteria: { cursor, limit: pageSize },
                                            reportingIds: reportingIds.length > 0 ? reportingIds : [],
                                        }).finally(() => {
                                            if (gridApi) gridApi.hideOverlay();
                                        });
                                    }
                                }}
                                onInputChange={(value) => setManagerSearchTerm(value)}
                                onFocus={() => fetchManagers({ variables: { roleId: parseInt(roleId) || 0, searchTerm: "", pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" } } })}
                                placeholder="Select Manager"
                                isClearable
                                isSearchable
                                isLoading={managersLoading}
                                filterOption={() => true}
                                className="w-full text-xs sm:text-sm"
                                menuPortalTarget={document.body}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '32px', height: '32px' }),
                                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                    input: (base) => ({ ...base, margin: '0', padding: '0' }),
                                    indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                                    menu: (base) => ({ ...base, minWidth: '350px', zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                    singleValue: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                }}
                            />
                            <SelectDropdown
                                options={superManagerOptions}
                                value={superManagerOptions.find(opt => opt.label === superManagerFilter) || null}
                                onChange={(selected) => {
                                    const newSuperManagerFilter = selected ? selected.label : "Select Super Manager";
                                    setSuperManagerFilter(newSuperManagerFilter);
                                    if (!selected) {
                                        if (gridApi) gridApi.showLoadingOverlay();
                                        const managerSelected = managerOptions.find(opt => opt.label === managerFilter);
                                        const reportingIds = managerSelected ? [parseInt(managerSelected.value)] : [];
                                        getRefetch({
                                            roleId: roleId || '',
                                            pagingCriteria: { cursor, limit: pageSize },
                                            reportingIds: reportingIds.length > 0 ? reportingIds : [],
                                        }).finally(() => {
                                            if (gridApi) gridApi.hideOverlay();
                                        });
                                    }
                                }}
                                onInputChange={(value) => setSuperManagerSearchTerm(value)}
                                onFocus={() => fetchSuperManagers({ variables: { roleId: parseInt(roleId) || 0, searchTerm: "", pagingCriteria: { limit: 50, cursor: null, sortByField: "created_at", sortOrder: "desc" } } })}
                                placeholder="Select Super Manager"
                                isClearable
                                isSearchable
                                isLoading={superManagersLoading}
                                filterOption={() => true}
                                className="w-full text-xs sm:text-sm"
                                menuPortalTarget={document.body}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '32px', height: '32px' }),
                                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                    input: (base) => ({ ...base, margin: '0', padding: '0' }),
                                    indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                                    menu: (base) => ({ ...base, minWidth: '350px', zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                    singleValue: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                }}
                            />
                            <button
                                type="button"
                                disabled={!managerOptions.find(opt => opt.label === managerFilter) && !superManagerOptions.find(opt => opt.label === superManagerFilter)}
                                onClick={handleApplyClick}
                                className={`px-4 py-1 sm:py-2 ${!managerOptions.find(opt => opt.label === managerFilter) && !superManagerOptions.find(opt => opt.label === superManagerFilter) ? 'bg-[#d9d9d9] cursor-not-allowed' : 'bg-[#AD0101] hover:bg-red-700'} text-white rounded font-inter text-xs sm:text-sm`}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end mb-1">
                <button
                    onClick={handleSaveClick}
                    className="px-4 py-2 bg-[#AD0101] hover:bg-red-700 text-white rounded font-inter text-sm"
                >
                    Save
                </button>
            </div>
            <div className="ag-theme-alpine h-[500px] w-full">
                <AgGridReact
                    rowData={filteredTableData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    serverSideStoreType="partial"
                    cacheBlockSize={pageSize}
                    maxBlocksInCache={10}
                    pagination={true}
                    paginationPageSize={pageSize}
                    onGridReady={onGridReady}
                    rowSelection="multiple"
                    onSelectionChanged={onSelectionChanged}
                    onFilterChanged={onFilterChanged}
                    suppressCellFocus={true}
                    animateRows={true}
                    groupDisplayType="multipleColumns"
                    suppressHorizontalScroll={true}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    loadingOverlayComponent={loadingOverlayComponent}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyle}
                    tooltipShowDelay={0}
                />
            </div>
        </div>
    );
};

export default RoleManagementView;